import { PropsWithChildren, forwardRef } from "react";
import { DDSTooltip } from "../../Overlays/DDSTooltip/DDSTooltip";
import classes from "./DDSIconButton.module.css";

export const DDSIconButtonClasses = classes;

export const Sizes = ["large", "medium", "small", "xsmall"] as const;
// `destructiveTertiary` was previously named `destructiveSecondary`. Renamed to match
// Figma and DDSButton (transparent bg + alpha hover + red icon).
export const Variants = [
  "primary",
  "primarySubtle",
  "secondary",
  "destructive",
  "destructiveTertiary",
  "tertiary",
  "gray",
  "transparent",
  "media",
  "white",
] as const;

type HTMLButtonProps = React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>;

type BaseProps = PropsWithChildren<{
  /**
   * The name will get used for automated product events.
   * @see https://www.notion.so/classdojo/Automatic-Product-Events-for-Web-bfc580f10a914c3ba514e5ec20f8ef9e?pvs=4
   */
  ["data-name"]?: string;
  /** The size of the icon button */
  size?: (typeof Sizes)[number];
  /** The visual style variant of the button */
  variant?: (typeof Variants)[number];
  /** Required for accessibility - describes the action the button performs */
  "aria-label": string;
  /**
   * Tooltip configuration. Prefer providing a tooltip — discoverability is the
   * default; only omit when the icon's meaning is self-evident in place (e.g.
   * paired with adjacent labeled controls). The accessible name comes from
   * `aria-label`, not the tooltip.
   */
  tooltip?: {
    /** Text to display in the tooltip */
    content: string;
    /** The name will get used for automated product events for the tooltip content.
     * @see https://www.notion.so/classdojo/Automatic-Product-Events-for-Web-bfc580f10a914c3ba514e5ec20f8ef9e?pvs=4
     */
    dataName: string;
    /** The side of the element to show the tooltip */
    position?: "top" | "right" | "bottom" | "left";
  };
  /** Experiments to track */
  experiments?: string[];
}>;

type IconButtonProps = BaseProps &
  Pick<HTMLButtonProps, "type" | "autoFocus" | "tabIndex"> & {
    /** Click handler for button */
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    /** Whether the button is disabled */
    disabled?: boolean;
    className?: string;
  };

export const DDSIconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      onClick,
      children,
      size = "medium",
      variant = "primary",
      disabled = false,
      type = "button",
      ["data-name"]: dataName,
      "aria-label": ariaLabel,
      tooltip,
      className,
      experiments,
      ...rest
    },
    ref,
  ) => {
    const buttonClassName = [
      className,
      classes.iconButton,
      classes[size],
      disabled ? classes.disabled : null,
      classes[variant],
    ]
      .filter(Boolean)
      .join(" ");

    const button = (
      <button
        data-name={dataName}
        data-experiments={experiments}
        type={type}
        className={buttonClassName}
        onClick={onClick}
        disabled={disabled}
        aria-label={ariaLabel}
        ref={ref}
        {...rest}
      >
        {children}
      </button>
    );

    if (!tooltip) return button;

    return (
      // As of 2025-12-04, the following is a known issue: if an element (e.g., button) has a tooltip,
      // and it's also a popover or dialog trigger, then when the popover or dialog is closed, focus is
      // programmatically returned to the trigger (which is good), and that causes the tooltip to
      // reappear (which is odd).
      // This can arise when using DDSIconButton to trigger a menu or dialog.
      // See https://linear.app/classdojo/issue/DDS-179/tooltip-reappears-when-its-trigger-doubles-as-a-popoverdialog-trigger.
      <DDSTooltip
        content={tooltip.content}
        data-name={tooltip.dataName}
        align="center"
        delayDuration={200}
        side={tooltip.position || "bottom"}
        showArrow
      >
        {button}
      </DDSTooltip>
    );
  },
);
